import { useState, useEffect, useRef, useCallback } from "react";
import { ChevronDownIcon } from "@heroicons/react/20/solid";
import { Link, useLocation } from "react-router-dom";
import { useTranslation } from 'react-i18next';
import "./Navbar.css";
import { getMegaPanels, getSimpleLinks } from "./nav/menuData";
import MegaMenuPanel from "./nav/MegaMenuPanel";

// Helper function to handle navigation
const NavLink = ({
  to,
  children,
  className,
  onClick,
}: {
  to: string | undefined;
  children: React.ReactNode;
  className?: string;
  onClick?: () => void;
}) => {
  const location = useLocation();

  if (!to) {
    return (
      <div className={className} onClick={onClick}>
        {children}
      </div>
    );
  }

  const isHashLink = to.startsWith("/#");

  if (isHashLink) {
    return (
      <a href={to.substring(1)} className={className} onClick={onClick}>
        {children}
      </a>
    );
  }

  return (
    <Link to={to} className={className} onClick={onClick}>
      {children}
    </Link>
  );
};

export default function Navbar() {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [expandedCategories, setExpandedCategories] = useState<string[]>([]);
  const [openPanelId, setOpenPanelId] = useState<string | null>(null);
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const navRef = useRef<HTMLElement>(null);
  // const { isAuthenticated, user, logout } = useAuth();
  const isAuthenticated = false;
  const user = { username: "John Doe", role: "admin" };
  const logout = () => {
    console.log("logout");
  };
  const { t } = useTranslation();

  // Get translated menu items
  const megaPanels = getMegaPanels(t);
  const simpleLinks = getSimpleLinks(t);
  const openPanel = megaPanels.find((panel) => panel.id === openPanelId) ?? null;

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const toggleCategory = (categoryName: string) => {
    setExpandedCategories((prev) =>
      prev.includes(categoryName)
        ? prev.filter((name) => name !== categoryName)
        : [...prev, categoryName]
    );
  };

  const isExpanded = (categoryName: string) =>
    expandedCategories.includes(categoryName);

  /* Mega menu open/close: hover with a small grace delay so the pointer can
     travel from the trigger to the panel, plus click/keyboard control. */
  const cancelClose = useCallback(() => {
    if (closeTimer.current) {
      clearTimeout(closeTimer.current);
      closeTimer.current = null;
    }
  }, []);

  const openPanelNow = useCallback(
    (id: string) => {
      cancelClose();
      setOpenPanelId(id);
    },
    [cancelClose]
  );

  const scheduleClose = useCallback(() => {
    cancelClose();
    closeTimer.current = setTimeout(() => setOpenPanelId(null), 160);
  }, [cancelClose]);

  const closePanel = useCallback(() => {
    cancelClose();
    setOpenPanelId(null);
  }, [cancelClose]);

  useEffect(() => () => cancelClose(), [cancelClose]);

  // Close on Escape or a click outside the navigation
  useEffect(() => {
    if (!openPanelId) return;

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") closePanel();
    };
    const onPointerDown = (event: MouseEvent) => {
      if (navRef.current && !navRef.current.contains(event.target as Node)) {
        closePanel();
      }
    };

    document.addEventListener("keydown", onKeyDown);
    document.addEventListener("mousedown", onPointerDown);
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      document.removeEventListener("mousedown", onPointerDown);
    };
  }, [openPanelId, closePanel]);

  // Close mobile menu when route changes
  const location = useLocation();
  useEffect(() => {
    setIsMobileMenuOpen(false);
    setExpandedCategories([]);
    setOpenPanelId(null);
  }, [location]);

  return (
    <nav
      ref={navRef}
      className="sticky top-0 z-50 bg-[#001F5F] dark:bg-[#001F5F] text-white"
      onBlur={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget as Node)) closePanel();
      }}
    >
      {/* Top Bar */}
      <div className="border-b border-white/10">
        <div className="container ">
          <div className="flex flex-col sm:flex-row justify-between items-center">
            <div className="flex flex-col sm:flex-row items-center space-y-2 sm:space-y-0 sm:space-x-4 mb-2 sm:mb-0">
              <div className="flex items-center my-3 sm:my-2">
                <svg
                  className="w-4 h-4 mr-2"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path d="M2 3a1 1 0 011-1h2.153a1 1 0 01.986.836l.74 4.435a1 1 0 01-.54 1.06l-1.548.773a11.037 11.037 0 006.105 6.105l.774-1.548a1 1 0 011.059-.54l4.435.74a1 1 0 01.836.986V17a1 1 0 01-1 1h-2C7.82 18 2 12.18 2 5V3z" />
                </svg>
                <span className="text-sm dark:text-gray-300 text-center sm:text-left">
                  (+252) 613737777 / 7777
                </span>
              </div>
              <div className="flex items-center">
                <svg
                  className="w-4 h-4 mr-2"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" />
                </svg>
                <span className="text-sm dark:text-gray-300">
                  08 AM - 4:30 PM WORKING HOURS
                </span>
              </div>
            </div>
            <div className="flex items-center space-x-4">
              <Link
                to="https://ib.mybank.so/mybank/"
                target="blank"
                className="text-sm hover:text-[#017EFF] dark:hover:text-blue-400"
              >
                <b>MyBank</b> Internet Banking
              </Link>
              <span className="text-white/30 hidden sm:inline">|</span>
              <a
                href="https://www.facebook.com/MyBankLTD/"
                target="_blank"
                className="text-blue-100 hover:text-white transition-colors"
              >
                <span className="sr-only">Facebook</span>
                <svg
                  className="w-4 h-4"
                  fill="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path d="M22 12c0-5.523-4.477-10-10-10S2 6.477 2 12c0 4.991 3.657 9.128 8.438 9.878v-6.987h-2.54V12h2.54V9.797c0-2.506 1.492-3.89 3.777-3.89 1.094 0 2.238.195 2.238.195v2.46h-1.26c-1.243 0-1.63.771-1.63 1.562V12h2.773l-.443 2.89h-2.33v6.988C18.343 21.128 22 16.991 22 12z" />
                </svg>
              </a>
              <a
                href="https://x.com/mybankltd?s=21"
                target="_blank"
                className="text-blue-100 hover:text-white transition-colors"
              >
                <span className="sr-only">Twitter</span>
                <svg
                  className="w-4 h-4"
                  fill="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path d="M23 3a10.9 10.9 0 01-3.14 1.53 4.48 4.48 0 00-7.86 3v1A10.66 10.66 0 013 4s-4 9 5 13a11.64 11.64 0 01-7 2c9 5 20 0 20-11.5a4.5 4.5 0 00-.08-.83A7.72 7.72 0 0023 3z" />
                </svg>
              </a>
              <a
                href="https://www.linkedin.com/company/mybankltd/"
                className="text-blue-100 hover:text-white transition-colors"
              >
                <span className="sr-only">LinkedIn</span>
                <svg
                  className="w-4 h-4"
                  fill="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z" />
                </svg>
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Main Navigation */}
      <div className="container">
        <div className="flex items-center justify-between h-20">
          <div className="flex items-center">
            <NavLink to="/" className="flex items-center">
              <div className="w-24 sm:w-30 h-16 sm:h-20 flex items-center justify-center mr-2">
                <img
                  src={"/assets/images/logo.svg"}
                  alt="MyBank logo"
                  className="object-contain w-full h-full"
                />
              </div>
            </NavLink>
          </div>

          {/* Desktop Navigation — mega-menu panels, lg and up */}
          <div className="hidden lg:flex items-center gap-1">
            <NavLink to="/" className="nav-link text-white hover:text-[#017EFF]">
              {t("nav.home")}
            </NavLink>

            {megaPanels.map((panel) => {
              const isOpen = openPanelId === panel.id;
              return (
                <div
                  key={panel.id}
                  className="relative"
                  onMouseEnter={() => openPanelNow(panel.id)}
                  onMouseLeave={scheduleClose}
                >
                  <button
                    type="button"
                    aria-expanded={isOpen}
                    aria-haspopup="true"
                    aria-controls={`mega-${panel.id}`}
                    onClick={() => (isOpen ? closePanel() : openPanelNow(panel.id))}
                    className={`nav-link gap-1 rounded-full px-3 transition-colors duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/70 ${
                      isOpen ? "bg-white/10 text-white" : "text-white hover:text-[#017EFF]"
                    }`}
                  >
                    {panel.label.toUpperCase()}
                    <ChevronDownIcon
                      aria-hidden="true"
                      className={`h-4 w-4 transition-transform duration-200 ${
                        isOpen ? "rotate-180" : ""
                      }`}
                    />
                  </button>
                </div>
              );
            })}

            {simpleLinks.map((link) => (
              <NavLink
                key={link.href}
                to={link.href}
                className="nav-link text-white hover:text-[#017EFF]"
              >
                {link.name.toUpperCase()}
              </NavLink>
            ))}

            <Link
              to="/open-account"
              className="btn-primary ml-2 py-2 px-4 bg-[#017EFF] hover:bg-[#0165CC] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/70"
            >
              {t('nav.openAccount')}
            </Link>
          </div>

          {/* Mobile menu button */}
          <div className="lg:hidden">
            <button
              onClick={toggleMobileMenu}
              className="mobile-menu-button p-2 rounded-md text-white hover:text-[#017EFF] hover:bg-white/10 focus:outline-none focus:ring-2 focus:ring-white/20"
              aria-expanded={isMobileMenuOpen}
              aria-label="Toggle menu"
            >
              <span className="sr-only">Open menu</span>
              <svg
                className="h-6 w-6"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d={
                    isMobileMenuOpen
                      ? "M6 18L18 6M6 6l12 12"
                      : "M4 6h16M4 12h16M4 18h16"
                  }
                />
              </svg>
            </button>

            {/* Mobile Menu Overlay */}
            <div
              className={`mobile-menu-overlay ${
                isMobileMenuOpen ? "open" : ""
              }`}
              onClick={toggleMobileMenu}
              aria-hidden="true"
            />

            {/* Mobile Menu Panel */}
            <div className={`mobile-menu ${isMobileMenuOpen ? "open" : ""}`}>
              <div className="p-4">
                <div className="flex items-center justify-between mb-6">
                  <NavLink
                    to="/"
                    className="flex items-center"
                    onClick={toggleMobileMenu}
                  >
                    <img
                      src={"/assets/images/logo blue.webp"}
                      alt="MyBank logo"
                      className="h-8"
                    />
                  </NavLink>
                  <button
                    onClick={toggleMobileMenu}
                    className="p-2 rounded-md text-gray-600 hover:text-[#017EFF] hover:bg-gray-50"
                    aria-label="Close menu"
                  >
                    <svg
                      className="h-6 w-6"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M6 18L18 6M6 6l12 12"
                      />
                    </svg>
                  </button>
                </div>

                <NavLink
                  to="/"
                  className="block px-3 py-2 text-base font-medium text-[#001F5F] hover:text-[#017EFF] hover:bg-gray-50 rounded-lg"
                  onClick={toggleMobileMenu}
                >
                  {t("nav.home")}
                </NavLink>

                {/* Section accordions built from the same model as the desktop mega menus */}
                {megaPanels.map((panel) => {
                  const groups = panel.columns.reduce(
                    (all, column) => all.concat(column),
                    [] as typeof panel.columns[number]
                  );
                  return (
                    <div key={panel.id} className="py-1">
                      <button
                        type="button"
                        onClick={() => toggleCategory(panel.id)}
                        aria-expanded={isExpanded(panel.id)}
                        className="w-full text-left px-3 py-3 text-base font-medium text-[#001F5F] hover:text-[#017EFF] hover:bg-gray-50 rounded-lg flex items-center justify-between"
                      >
                        {panel.label}
                        <ChevronDownIcon
                          aria-hidden="true"
                          className={`h-5 w-5 transform transition-transform ${
                            isExpanded(panel.id) ? "rotate-180" : ""
                          }`}
                        />
                      </button>
                      <div
                        className={`mt-1 space-y-2 ${
                          isExpanded(panel.id) ? "block" : "hidden"
                        }`}
                      >
                        {panel.href && (
                          <NavLink
                            to={panel.href}
                            className="block px-3 py-2 text-sm font-semibold text-[#017EFF] hover:underline"
                            onClick={toggleMobileMenu}
                          >
                            {panel.intro?.ctaLabel ?? `View ${panel.label}`}
                          </NavLink>
                        )}
                        {groups.map((group) => (
                          <div key={group.heading}>
                            <p className="px-3 pt-2 text-[11px] font-semibold uppercase tracking-[0.14em] text-gray-400">
                              {group.heading}
                            </p>
                            <ul className="mt-1">
                              {group.links.map((link) => {
                                const Icon = link.icon;
                                return (
                                  <li key={link.href + link.name}>
                                    <NavLink
                                      to={link.href}
                                      className="flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm text-[#001F5F] hover:bg-gray-50 hover:text-[#017EFF]"
                                      onClick={toggleMobileMenu}
                                    >
                                      <span className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg bg-[#EFF5FF] text-[#017EFF]">
                                        <Icon className="h-4 w-4" aria-hidden="true" />
                                      </span>
                                      {link.name}
                                    </NavLink>
                                  </li>
                                );
                              })}
                            </ul>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })}

                {simpleLinks.map((link) => (
                  <NavLink
                    key={link.href}
                    to={link.href}
                    className="block px-3 py-3 text-base font-medium text-[#001F5F] hover:text-[#017EFF] hover:bg-gray-50 rounded-lg"
                    onClick={toggleMobileMenu}
                  >
                    {link.name}
                  </NavLink>
                ))}

                {/* Mobile Authentication Links */}
                <div className="border-t border-gray-200 mt-4 pt-4">
                  {isAuthenticated ? (
                    <div className="space-y-2">
                      <div className="px-3 py-2 text-sm text-gray-600">
                        Welcome, {user?.username}
                      </div>
                      {user?.role === "admin" && (
                        <NavLink
                          to="/admin"
                          className="block px-3 py-2 text-base font-medium text-[#001F5F] hover:text-[#017EFF] hover:bg-gray-50 rounded-lg"
                          onClick={toggleMobileMenu}
                        >
                          Admin Dashboard
                        </NavLink>
                      )}
                      <button
                        onClick={() => {
                          logout();
                          toggleMobileMenu();
                        }}
                        className="block w-full text-left px-3 py-2 text-base font-medium text-red-600 hover:text-red-700 hover:bg-red-50 rounded-lg"
                      >
                        Logout
                      </button>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {/* <NavLink
                        to="/login"
                        className="block px-3 py-2 text-base font-medium text-[#001F5F] hover:text-[#017EFF] hover:bg-gray-50 rounded-lg"
                        onClick={toggleMobileMenu}
                      >
                        {t("nav.login")}
                      </NavLink>
                      <NavLink
                        to="/register"
                        className="block px-3 py-2 text-base font-medium text-[#001F5F] hover:text-[#017EFF] hover:bg-gray-50 rounded-lg"
                        onClick={toggleMobileMenu}
                      >
                        {t("nav.register")}
                      </NavLink> */}
                    </div>
                  )}
                </div>

                <Link
                  to="/open-account"
                  className="block w-full text-center mt-4 py-3 px-4 bg-[#017EFF] text-white rounded-lg hover:bg-[#0165CC] transition-colors duration-200"
                  onClick={toggleMobileMenu}
                >
                  {t("nav.openAccount")}
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Desktop mega-menu panel */}
      {openPanel && (
        <div
          id={`mega-${openPanel.id}`}
          className="absolute inset-x-0 top-full hidden lg:block"
          onMouseEnter={cancelClose}
          onMouseLeave={scheduleClose}
        >
          <MegaMenuPanel panel={openPanel} onNavigate={closePanel} />
        </div>
      )}
    </nav>
  );
}
